import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  TrophyIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { LeagueService, TeamService, UserService } from '../../../services/firebaseService';

export default function LeagueDetail() {
  const { leagueId } = useParams();
  const [league, setLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [manager, setManager] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (leagueId) {
      loadLeagueData();
    }
  }, [leagueId]);

  const loadLeagueData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [leagueData, leagueSeasons] = await Promise.all([
        LeagueService.getLeague(leagueId),
        LeagueService.getLeagueSeasons(leagueId)
      ]);

      if (!leagueData) {
        throw new Error('League not found');
      }

      // Fetch actual team count per season from teams subcollection (draftOrder can be stale/wrong)
      const seasonsWithCounts = await Promise.all(
        leagueSeasons.map(async (s) => {
          const teamCount = await TeamService.getTeamCount(leagueId, s.id);
          return { ...s, teamCount };
        })
      );

      setLeague(leagueData);
      setSeasons(seasonsWithCounts);

      // Fetch manager data if managerId exists
      if (leagueData.managerId) {
        try {
          const managerData = await UserService.getUser(leagueData.managerId);
          setManager(managerData);
        } catch (managerError) {
          console.warn('Could not load manager data:', managerError);
          setManager(null);
        }
      } else {
        setManager(null);
      }
    } catch (error) {
      console.error('Error loading league data:', error);
      setError(error.message || 'Failed to load league data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'upcoming':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading league details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading League</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-x-4">
            <button
              onClick={loadLeagueData}
              className="px-4 py-2 bg-survivor-600 text-white rounded-md hover:bg-survivor-700"
            >
              Try Again
            </button>
            <Link
              to="/admin/leagues"
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Back to Leagues
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">League Not Found</h2>
          <p className="text-gray-600 mb-4">The requested league could not be found.</p>
          <Link
            to="/admin/leagues"
            className="px-4 py-2 bg-survivor-600 text-white rounded-md hover:bg-survivor-700"
          >
            Back to Leagues
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center">
            <Link
              to="/admin/leagues"
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-8 w-8 text-gray-400 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{league.name}</h1>
                <p className="mt-1 text-sm text-gray-500">
                  {league.description || 'No description available'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* League Stats */}
        <div className="px-4 py-6 sm:px-0 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CalendarIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Seasons
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {seasons.length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserGroupIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Max Teams
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {league.maxTeams || 'Unlimited'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrophyIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Current Teams
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {league.currentTeams || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Status
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(league.status)}`}>
                          {league.status}
                        </span>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* League Details */}
        <div className="px-4 py-6 sm:px-0 mb-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">League Information</h2>
            </div>
            <div className="px-6 py-4">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(league.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(league.updatedAt)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Manager</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {manager ? (
                      <div>
                        <div className="text-survivor-600 font-medium">{manager.displayName}</div>
                        <div className="text-gray-500">{manager.email}</div>
                      </div>
                    ) : league.managerId ? (
                      <span className="text-gray-500">Manager data unavailable</span>
                    ) : (
                      <span className="text-gray-500">No manager assigned</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Privacy</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {league.isPrivate ? 'Private' : 'Public'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        {/* Seasons List */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Seasons</h2>
            </div>
            
            {seasons.length === 0 ? (
              <div className="text-center py-12">
                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No seasons</h3>
                <p className="mt-1 text-sm text-gray-500">
                  This league hasn't participated in any seasons yet.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {seasons.map((leagueSeason) => (
                  <li key={leagueSeason.seasonId}>
                    <Link
                      to={`/admin/leagues/${leagueId}/seasons/${leagueSeason.seasonId}`}
                      className="block hover:bg-gray-50 px-6 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <TrophyIcon className="h-8 w-8 text-survivor-600" />
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-survivor-600 truncate">
                                Season {leagueSeason.seasonId}
                              </p>
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(leagueSeason.isActive ? 'active' : 'completed')}`}>
                                {leagueSeason.isActive ? 'Active' : 'Completed'}
                              </span>
                            </div>
                            <div className="mt-1">
                              <p className="text-sm text-gray-500">
                                Draft Status: {leagueSeason.draftStatus}
                              </p>
                            </div>
                            <div className="mt-1 flex items-center text-sm text-gray-500">
                              <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                              <span>Invitation Code: {leagueSeason.invitationCode}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              {leagueSeason.teamCount ?? 0} teams
                            </p>
                            <p className="text-sm text-gray-500">
                              Draft: {leagueSeason.draftStatus}
                            </p>
                          </div>
                          <div className="ml-4">
                            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
